/**
 * Payables client-side gate.
 *
 * Two rules that are easy to get wrong, so they live here once:
 *
 *  1. The owner check is `role === 'owner' || permissions.includes(perm)`, NOT
 *     overrides-only. /api/me emits per-user OVERRIDE permissions, while the
 *     server authorizes with role-defaults ∪ overrides — so an owner's list is
 *     usually empty and a naive includes() check would lock them out.
 *  2. There is NO writes_enabled term. Payables makes no ERP write, so the
 *     only gates are the feature flag and the permission.
 */
import type { User } from '../api/types';

export interface PayablesPerms {
  enabled: boolean;
  canView: boolean;
  canCapture: boolean;
  canPay: boolean;
}

export function payablesPerms(user: User | null | undefined): PayablesPerms {
  const enabled = !!user?.features?.payables_enabled;
  if (!user || !enabled) {
    return { enabled: false, canView: false, canCapture: false, canPay: false };
  }
  const has = (perm: string) =>
    user.role === 'owner' || (user.permissions ?? []).includes(perm);
  return {
    enabled: true,
    canView: has('ap:view'),
    canCapture: has('ap:capture'),
    canPay: has('ap:pay'),
  };
}
